import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  Chip,
  IconButton,
  Button,
  Tooltip,
  useTheme,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TireInventoryHandler from 'handlers/tire-inventory.handler';
import { CustomMuiThemeProvider } from 'theme/CustomMuiThemeProvider';
import { useAppSelector } from 'store/hook';
import { type TireRecommendationResponse } from 'types/tire-timeline.types';
import { useTranslation } from 'react-i18next';
import { clientThemePalette, CLIENT_THEME } from 'utils/clientTheme';

// ─── Constants ───────────────────────────────────────────────────────────────
const DIAG_STEPS = [
  'Gathering pressure sensor data…',
  'Analysing pressure trends…',
  'Gathering tread wear measurements…',
  'Analysing tread depth patterns…',
  'Gathering leakage & valve data…',
  'Analysing leakage risk factors…',
  'Gathering all inspection records…',
  'Analysing inspection history…',
  'Correlating telemetry with fleet data…',
  'Generating AI health prediction…',
];
const INSIGHTS: Array<{
  severity: 'critical' | 'warning' | 'info';
  stat: string;
  headline: string;
  detail: string;
}> = [
  {
    severity: 'critical',
    stat: '90%',
    headline: 'of tire failures are completely preventable',
    detail:
      'Underinflation is the #1 cause of tire failure in heavy equipment — and the easiest to catch with regular monitoring.',
  },
  {
    severity: 'warning',
    stat: '5–10×',
    headline: 'the cost of a blowout vs. routine inspection',
    detail:
      'Downtime, towing, and emergency replacement add up fast. Proactive health checks pay for themselves every time.',
  },
  {
    severity: 'critical',
    stat: '+40%',
    headline: 'longer stopping distance on worn tread',
    detail:
      'Below 4mm of tread on wet roads, braking performance collapses. That margin can be the difference between a near-miss and a collision.',
  },
  {
    severity: 'warning',
    stat: '25%',
    headline: 'more heat when running 20% underinflated',
    detail:
      'Excess heat silently degrades rubber from the inside — accelerating structural wear long before any visible damage appears.',
  },
  {
    severity: 'info',
    stat: '20%',
    headline: 'pressure loss before a tire even looks flat',
    detail:
      "Most pressure loss is invisible. By the time it's obvious, significant damage has already been done to the casing.",
  },
];

const INSIGHT_SEVERITY_CONFIG = {
  critical: { color: '#f44336', label: 'CRITICAL RISK' },
  warning: { color: '#ff9800', label: 'WARNING' },
  info: { color: '#2196f3', label: 'DID YOU KNOW' },
};

function getHealthColor(score: number): string {
  if (score >= 80) return '#4caf50';
  if (score >= 60) return '#ff9800';
  return '#f44336';
}

function getGradeColor(grade: string): string {
  if (grade === 'A') return '#4caf50';
  if (grade === 'B') return '#8bc34a';
  if (grade === 'C') return '#ff9800';
  if (grade === 'D') return '#ff5722';
  return '#f44336';
}

function getRiskColor(risk: string): string {
  if (risk === 'low') return '#4caf50';
  if (risk === 'medium') return '#ff9800';
  return '#f44336';
}

function getTrendColor(trend: string): string {
  if (trend.includes('drop') || trend.includes('decrease')) return '#f44336';
  if (trend.includes('stable')) return '#4caf50';
  return '#ff9800';
}

// ─── Props ───────────────────────────────────────────────────────────────────
interface TireAIPredictionModalProps {
  open: boolean;
  onClose: () => void;
  customerId: string;
  assetId: string;
  tirePosition: string;
  /** When navigating from the serial-number flow, pass the serial number to show it in the header. */
  tireSerialNumber?: string;
}

// ─── Component ───────────────────────────────────────────────────────────────
export default function TireAIPredictionModal({
  open,
  onClose,
  customerId,
  assetId,
  tirePosition,
  tireSerialNumber,
}: TireAIPredictionModalProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const bg = theme.palette.background.default;
  const cardBg =
    theme.palette.customBackground?.section ?? theme.palette.background.paper;
  const borderColor = theme.palette.divider;
  const textPrimary = theme.palette.text.primary;
  const textSecondary = theme.palette.text.secondary;

  // ── Resolve assetId → bumperNumber ────────────────────────────────────────
  const assets = useAppSelector((state) => state.assetReducer.assets);
  const bumperNumber = useMemo(() => {
    const asset = assets.find((a) => a.assetId === assetId);
    return asset?.bumperNumber ?? assetId;
  }, [assetId, assets]);

  const [status, setStatus] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');
  const [data, setData] = useState<TireRecommendationResponse | null>(null);
  const [insightIndex, setInsightIndex] = useState(0);
  const [insightVisible, setInsightVisible] = useState(true);
  const [diagStep, setDiagStep] = useState(0);
  const [diagVisible, setDiagVisible] = useState(true);

  // ── Handler — created each render, same as the rest of the codebase ────────
  // BaseHandler calls useAppDispatch() in its constructor; wrapping in useMemo
  // would cause a hooks-order violation (useContext only fires on first render).
  const handler = new TireInventoryHandler();

  // ── Fetch prediction ───────────────────────────────────────────────────────
  const fetchPrediction = useCallback(
    (onIgnore: () => boolean) => {
      setStatus('loading');
      setData(null);
      void handler
        .getTireRecommendation(customerId, assetId, tirePosition)
        .then((result) => {
          if (onIgnore()) return;
          if (result) {
            setData(result);
            setStatus('success');
          } else {
            setStatus('error');
          }
        });
    },
    [customerId, assetId, tirePosition]
  );

  // ── Fetch on open ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    let ignored = false;
    fetchPrediction(() => ignored);
    const palette = clientThemePalette[CLIENT_THEME].light;
    return () => {
      ignored = true;
    };
  }, [open, fetchPrediction]);

  // ── Reset on close ─────────────────────────────────────────────────────────
  const handleClose = () => {
    if (status === 'loading') return;
    setStatus('idle');
    setData(null);
    onClose();
  };

  // ── Cycle insights ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (status !== 'loading') return;
    let timeoutId: ReturnType<typeof setTimeout>;
    const interval = setInterval(() => {
      setInsightVisible(false);
      timeoutId = setTimeout(() => {
        setInsightIndex((i) => (i + 1) % INSIGHTS.length);
        setInsightVisible(true);
      }, 400);
    }, 3000);
    return () => {
      clearInterval(interval);
      clearTimeout(timeoutId);
    };
  }, [status]);

  // ── Cycle diagnostic step text ─────────────────────────────────────────────
  useEffect(() => {
    if (status !== 'loading') {
      setDiagStep(0);
      setDiagVisible(true);
      return;
    }
    let timeoutId: ReturnType<typeof setTimeout>;
    const interval = setInterval(() => {
      setDiagVisible(false);
      timeoutId = setTimeout(() => {
        setDiagStep((s) => (s + 1) % DIAG_STEPS.length);
        setDiagVisible(true);
      }, 300);
    }, 1800);
    return () => {
      clearInterval(interval);
      clearTimeout(timeoutId);
    };
  }, [status]);

  const rec = data?.recommendation;

  return (
    <CustomMuiThemeProvider>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown={status === 'loading'}
        PaperProps={{
          sx: {
            backgroundColor: bg,
            borderRadius: '16px',
            border: `1px solid ${borderColor}`,
            backgroundImage: 'none',
          },
        }}
      >
        {/* ── Dialog Title ── */}
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderBottom: `1px solid ${borderColor}`,
            pb: 2,
          }}
        >
          <AutoAwesomeIcon sx={{ color: '#9c27b0', fontSize: 22 }} />
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: textPrimary, flex: 1 }}
          >
            {t('ai_tire_prediction')}
          </Typography>
          {tireSerialNumber != null ? (
            <Chip
              label={tireSerialNumber}
              size="small"
              sx={{
                backgroundColor: 'rgba(33,150,243,0.15)',
                color: '#2196f3',
                fontWeight: 600,
                border: '1px solid rgba(33,150,243,0.3)',
              }}
            />
          ) : (
            <Chip
              label={bumperNumber}
              size="small"
              sx={{
                backgroundColor: 'rgba(76,175,80,0.15)',
                color: '#4caf50',
                fontWeight: 600,
                border: '1px solid rgba(76,175,80,0.3)',
              }}
            />
          )}
          {status !== 'loading' && (
            <IconButton
              size="small"
              onClick={handleClose}
              sx={{ color: textSecondary }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {/* ── Loading State ── */}
          {status === 'loading' && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                py: 5,
                px: 4,
                gap: 4,
                minHeight: 360,
              }}
            >
              {/* ── Radar scanner ── */}
              <Box sx={{ position: 'relative', width: 110, height: 110 }}>
                {/* Outer slow-spinning dashed ring */}
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '50%',
                    border: '2px dashed rgba(156,39,176,0.35)',
                    '@keyframes spinSlow': {
                      to: { transform: 'rotate(360deg)' },
                    },
                    animation: 'spinSlow 6s linear infinite',
                  }}
                />
                {/* Middle pulsing ring */}
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 14,
                    borderRadius: '50%',
                    border: '1.5px solid rgba(156,39,176,0.25)',
                    '@keyframes ringPulse': {
                      '0%,100%': { transform: 'scale(1)', opacity: 0.25 },
                      '50%': { transform: 'scale(1.08)', opacity: 0.7 },
                    },
                    animation: 'ringPulse 2s ease-in-out infinite',
                  }}
                />
                {/* Radar sweep */}
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '50%',
                    background:
                      'conic-gradient(from 0deg, transparent 0deg, rgba(156,39,176,0.18) 70deg, transparent 71deg)',
                    '@keyframes radarSpin': {
                      to: { transform: 'rotate(360deg)' },
                    },
                    animation: 'radarSpin 1.8s linear infinite',
                  }}
                />
                {/* Cross-hair lines */}
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      width: '70%',
                      height: '1px',
                      backgroundColor: 'rgba(156,39,176,0.15)',
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      width: '1px',
                      height: '70%',
                      backgroundColor: 'rgba(156,39,176,0.15)',
                    },
                  }}
                />
                {/* Center pulsing dot */}
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      backgroundColor: '#9c27b0',
                      boxShadow: '0 0 12px 4px rgba(156,39,176,0.45)',
                      '@keyframes dotPulse': {
                        '0%,100%': { transform: 'scale(1)', opacity: 1 },
                        '50%': { transform: 'scale(1.5)', opacity: 0.6 },
                      },
                      animation: 'dotPulse 1.4s ease-in-out infinite',
                    }}
                  />
                </Box>
              </Box>

              {/* Scanning label */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#9c27b0',
                    fontWeight: 700,
                    letterSpacing: 3,
                    textTransform: 'uppercase',
                    display: 'block',
                  }}
                >
                  Analyzing
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: textSecondary,
                    display: 'block',
                    minHeight: '1.4em',
                    opacity: diagVisible ? 1 : 0,
                    transition: 'opacity 0.3s ease',
                  }}
                >
                  {DIAG_STEPS[diagStep]}
                </Typography>
              </Box>

              {/* ── Insight card ── */}
              {(() => {
                const insight = INSIGHTS[insightIndex];
                const cfg = INSIGHT_SEVERITY_CONFIG[insight.severity];
                return (
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: 460,
                      borderRadius: '14px',
                      border: `1px solid ${cfg.color}44`,
                      backgroundColor: `${cfg.color}0d`,
                      overflow: 'hidden',
                      transition: 'opacity 0.4s ease',
                      opacity: insightVisible ? 1 : 0,
                    }}
                  >
                    {/* Severity header */}
                    <Box
                      sx={{
                        px: 2,
                        py: 0.6,
                        backgroundColor: `${cfg.color}22`,
                        borderBottom: `1px solid ${cfg.color}33`,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: cfg.color,
                          fontWeight: 800,
                          letterSpacing: 1.5,
                          textTransform: 'uppercase',
                          fontSize: '0.65rem',
                        }}
                      >
                        {cfg.label}
                      </Typography>
                    </Box>
                    {/* Body */}
                    <Box
                      sx={{
                        px: 2.5,
                        py: 2,
                        display: 'flex',
                        gap: 2,
                        alignItems: 'flex-start',
                      }}
                    >
                      {/* Big stat */}
                      <Typography
                        sx={{
                          fontSize: '2.2rem',
                          fontWeight: 900,
                          color: cfg.color,
                          lineHeight: 1,
                          flexShrink: 0,
                          minWidth: 64,
                        }}
                      >
                        {insight.stat}
                      </Typography>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            color: textPrimary,
                            mb: 0.4,
                            lineHeight: 1.3,
                          }}
                        >
                          {insight.headline}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: textSecondary,
                            lineHeight: 1.5,
                            display: 'block',
                          }}
                        >
                          {insight.detail}
                        </Typography>
                      </Box>
                    </Box>
                    {/* Progress dots */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 0.6,
                        pb: 1.5,
                      }}
                    >
                      {INSIGHTS.map((_, i) => (
                        <Box
                          key={i}
                          sx={{
                            width: i === insightIndex ? 18 : 6,
                            height: 6,
                            borderRadius: 3,
                            backgroundColor:
                              i === insightIndex ? cfg.color : `${cfg.color}44`,
                            transition: 'all 0.3s ease',
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                );
              })()}
            </Box>
          )}

          {/* ── Error State ── */}
          {status === 'error' && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                py: 6,
                gap: 2,
              }}
            >
              <Typography variant="body1" sx={{ color: '#f44336' }}>
                {t('failed_to_load_ai_prediction')}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                sx={{
                  color: '#9c27b0',
                  borderColor: 'rgba(156,39,176,0.4)',
                  textTransform: 'none',
                }}
                onClick={() => {
                  fetchPrediction(() => false);
                }}
              >
                Retry
              </Button>
            </Box>
          )}

          {/* ── Success State ── */}
          {status === 'success' && rec != null && data != null && (
            <Box sx={{ p: 3 }}>
              {/* Health score + grade row */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
                  mb: 3,
                  p: 2.5,
                  backgroundColor: cardBg,
                  borderRadius: '12px',
                  border: `1px solid ${borderColor}`,
                }}
              >
                {/* Circular gauge */}
                <Box sx={{ position: 'relative', flexShrink: 0 }}>
                  <Box
                    sx={{
                      width: 90,
                      height: 90,
                      borderRadius: '50%',
                      background: `conic-gradient(${getHealthColor(
                        rec.health_score
                      )} ${rec.health_score * 3.6}deg, rgba(128,128,128,0.2) ${
                        rec.health_score * 3.6
                      }deg)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        width: 68,
                        height: 68,
                        borderRadius: '50%',
                        backgroundColor: cardBg,
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: '1.4rem',
                        // color: getHealthColor(rec.health_score),
                        position: 'relative',
                        zIndex: 1,
                      }}
                    >
                      {rec.health_score}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.4,
                      mt: 0.5,
                    }}
                  >
                    <Typography variant="caption" sx={{ color: textSecondary }}>
                      {t('health_score')}
                    </Typography>
                    <Tooltip
                      arrow
                      placement="right"
                      title={
                        <Box sx={{ maxWidth: 300, p: 0.5 }}>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.75rem',
                              mb: 0.8,
                              color: '#fff',
                            }}
                          >
                            {t('how_is_the_health_score_calculated')}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.7rem',
                              color: 'rgba(255,255,255,0.8)',
                              mb: 1.2,
                              lineHeight: 1.6,
                            }}
                          >
                            {t('starts_at')} <strong>100</strong>{' '}
                            {t('and_deducts_points_for')}
                          </Typography>
                          {[
                            ['Slow leak event', '−2 pts each'],
                            ['Fast leak event', '−6 pts each'],
                            ['Active unresolved leak', '−5 pts each'],
                            ['Pressure out-of-range >10%', '−5 pts'],
                            ['Tread depth <4 mm', '−10 pts'],
                          ].map(([label, pts]) => (
                            <Box
                              key={label}
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: 2,
                                mb: 0.4,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: '0.68rem',
                                  color: 'rgba(255,255,255,0.75)',
                                }}
                              >
                                {label}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: '0.68rem',
                                  color: '#ff7043',
                                  fontWeight: 700,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {pts}
                              </Typography>
                            </Box>
                          ))}
                          <Box
                            sx={{
                              borderTop: '1px solid rgba(255,255,255,0.15)',
                              mt: 1.2,
                              pt: 1.2,
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 700,
                                fontSize: '0.72rem',
                                color: '#fff',
                                mb: 0.6,
                              }}
                            >
                              {t('health_grade')}
                            </Typography>
                            {[
                              ['A', '90–100', '#4caf50', 'Excellent'],
                              ['B', '75–89', '#8bc34a', 'Good — monitor'],
                              [
                                'C',
                                '55–74',
                                '#ff9800',
                                'Fair — plan maintenance',
                              ],
                              [
                                'D',
                                '35–54',
                                '#ff5722',
                                'Poor — schedule replacement',
                              ],
                              [
                                'F',
                                '0–34',
                                '#f44336',
                                'Critical — replace immediately',
                              ],
                            ].map(([grade, range, color, meaning]) => (
                              <Box
                                key={grade}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  mb: 0.35,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: '0.68rem',
                                    fontWeight: 800,
                                    color,
                                    minWidth: 12,
                                  }}
                                >
                                  {grade}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: '0.68rem',
                                    color: 'rgba(255,255,255,0.5)',
                                    minWidth: 44,
                                  }}
                                >
                                  {range}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: '0.68rem',
                                    color: 'rgba(255,255,255,0.75)',
                                  }}
                                >
                                  {meaning}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      }
                    >
                      <InfoOutlinedIcon
                        sx={{
                          fontSize: '13px',
                          color: textSecondary,
                          cursor: 'help',
                          opacity: 0.7,
                          '&:hover': { opacity: 1 },
                          transition: 'opacity 0.15s',
                        }}
                      />
                    </Tooltip>
                  </Box>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      mb: 1,
                    }}
                  >
                    <Chip
                      label={`Grade ${rec.health_grade}`}
                      sx={{
                        backgroundColor: `${getGradeColor(rec.health_grade)}22`,
                        color: getGradeColor(rec.health_grade),
                        fontWeight: 800,
                        fontSize: '1rem',
                        border: `1px solid ${getGradeColor(
                          rec.health_grade
                        )}66`,
                        height: 36,
                      }}
                    />
                    <Chip
                      label={`Analysis: ${data.analysis_date}`}
                      size="small"
                      sx={{
                        color: textSecondary,
                        border: `1px solid ${borderColor}`,
                        backgroundColor: 'transparent',
                      }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ color: textSecondary }}>
                    {t('tire_position_2')}{' '}
                    <strong style={{ color: textPrimary }}>
                      {data.tire_position.replace(/-/g, ' ')}
                    </strong>
                    {' · '}
                    {t('asset')}{' '}
                    <strong style={{ color: textPrimary }}>
                      {bumperNumber}
                    </strong>
                  </Typography>
                </Box>
              </Box>

              {/* Assessment cards */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                {/* Tread */}
                <Box
                  sx={{
                    flex: '1 1 200px',
                    backgroundColor: cardBg,
                    border: `1px solid ${borderColor}`,
                    borderRadius: '12px',
                    p: 2,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: textPrimary, mb: 1.5 }}
                  >
                    {t('tread_assessment')}
                  </Typography>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}
                  >
                    <Box
                      sx={{ display: 'flex', justifyContent: 'space-between' }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: textSecondary }}
                      >
                        Depth
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: textPrimary, fontWeight: 600 }}
                      >
                        {rec.tread_assessment.current_depth_mm} mm
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: 'flex', justifyContent: 'space-between' }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: textSecondary }}
                      >
                        {t('status')}
                      </Typography>
                      <Chip
                        label={rec.tread_assessment.status}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          backgroundColor:
                            rec.tread_assessment.status === 'good'
                              ? 'rgba(76,175,80,0.15)'
                              : 'rgba(255,152,0,0.15)',
                          color:
                            rec.tread_assessment.status === 'good'
                              ? '#4caf50'
                              : '#ff9800',
                          border: 'none',
                        }}
                      />
                    </Box>
                    <Box
                      sx={{ display: 'flex', justifyContent: 'space-between' }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: textSecondary }}
                      >
                        {t('wear_rate')}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: textPrimary, fontWeight: 600 }}
                      >
                        {rec.tread_assessment.wear_rate_mm_per_month} mm/mo
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: 'flex', justifyContent: 'space-between' }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: textSecondary }}
                      >
                        {t('est_months_left')}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: textPrimary, fontWeight: 600 }}
                      >
                        {rec.tread_assessment.estimated_months_remaining}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: 'flex', justifyContent: 'space-between' }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: textSecondary }}
                      >
                        Pattern
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: textPrimary,
                          fontWeight: 600,
                          textTransform: 'capitalize',
                        }}
                      >
                        {rec.tread_assessment.wear_pattern}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      mt: 1.5,
                      pt: 1.5,
                      borderTop: `1px solid ${borderColor}`,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: textSecondary,
                        fontStyle: 'italic',
                        lineHeight: 1.4,
                      }}
                    >
                      {rec.tread_assessment.action}
                    </Typography>
                  </Box>
                </Box>

                {/* Pressure */}
                <Box
                  sx={{
                    flex: '1 1 200px',
                    backgroundColor: cardBg,
                    border: `1px solid ${borderColor}`,
                    borderRadius: '12px',
                    p: 2,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: textPrimary, mb: 1.5 }}
                  >
                    {t('pressure_assessment')}
                  </Typography>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}
                  >
                    <Box
                      sx={{ display: 'flex', justifyContent: 'space-between' }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: textSecondary }}
                      >
                        Current
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: textPrimary, fontWeight: 600 }}
                      >
                        {rec.pressure_assessment.current_kpa} kPa
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: 'flex', justifyContent: 'space-between' }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: textSecondary }}
                      >
                        {t('status')}
                      </Typography>
                      <Chip
                        label={rec.pressure_assessment.status}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          backgroundColor:
                            rec.pressure_assessment.status === 'normal'
                              ? 'rgba(76,175,80,0.15)'
                              : 'rgba(244,67,54,0.15)',
                          color:
                            rec.pressure_assessment.status === 'normal'
                              ? '#4caf50'
                              : '#f44336',
                          border: 'none',
                        }}
                      />
                    </Box>
                    <Box
                      sx={{ display: 'flex', justifyContent: 'space-between' }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: textSecondary }}
                      >
                        Trend
                      </Typography>
                      <Chip
                        label={rec.pressure_assessment.trend.replace(/_/g, ' ')}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          backgroundColor: `${getTrendColor(
                            rec.pressure_assessment.trend
                          )}22`,
                          color: getTrendColor(rec.pressure_assessment.trend),
                          border: 'none',
                          textTransform: 'capitalize',
                        }}
                      />
                    </Box>
                    <Box
                      sx={{ display: 'flex', justifyContent: 'space-between' }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: textSecondary }}
                      >
                        {t('out_of_range_last_mo')}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: textPrimary, fontWeight: 600 }}
                      >
                        {rec.pressure_assessment.out_of_range_pct_last_month}%
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      mt: 1.5,
                      pt: 1.5,
                      borderTop: `1px solid ${borderColor}`,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: textSecondary,
                        fontStyle: 'italic',
                        lineHeight: 1.4,
                      }}
                    >
                      {rec.pressure_assessment.action}
                    </Typography>
                  </Box>
                </Box>

                {/* Leakage */}
                <Box
                  sx={{
                    flex: '1 1 200px',
                    backgroundColor: cardBg,
                    border: `1px solid ${borderColor}`,
                    borderRadius: '12px',
                    p: 2,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: textPrimary, mb: 1.5 }}
                  >
                    {t('leakage_assessment')}
                  </Typography>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}
                  >
                    <Box
                      sx={{ display: 'flex', justifyContent: 'space-between' }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: textSecondary }}
                      >
                        {t('total_events_2')}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: textPrimary, fontWeight: 600 }}
                      >
                        {rec.leakage_assessment.total_events}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: 'flex', justifyContent: 'space-between' }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: textSecondary }}
                      >
                        {t('active_leaks')}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: textPrimary, fontWeight: 600 }}
                      >
                        {rec.leakage_assessment.active_leaks}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: 'flex', justifyContent: 'space-between' }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: textSecondary }}
                      >
                        {t('risk_level')}
                      </Typography>
                      <Chip
                        label={rec.leakage_assessment.risk_level}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          backgroundColor: `${getRiskColor(
                            rec.leakage_assessment.risk_level
                          )}22`,
                          color: getRiskColor(
                            rec.leakage_assessment.risk_level
                          ),
                          border: 'none',
                          textTransform: 'capitalize',
                        }}
                      />
                    </Box>
                    <Box sx={{ mt: 0.5 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: textSecondary, fontStyle: 'italic' }}
                      >
                        {rec.leakage_assessment.pattern}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      mt: 1.5,
                      pt: 1.5,
                      borderTop: `1px solid ${borderColor}`,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: textSecondary,
                        fontStyle: 'italic',
                        lineHeight: 1.4,
                      }}
                    >
                      {rec.leakage_assessment.action}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Overall recommendation */}
              <Box
                sx={{
                  p: 2.5,
                  backgroundColor: 'rgba(156,39,176,0.06)',
                  border: '1px solid rgba(156,39,176,0.25)',
                  borderRadius: '12px',
                }}
              >
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <AutoAwesomeIcon sx={{ color: '#9c27b0', fontSize: 18 }} />
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: '#9c27b0' }}
                  >
                    {t('overall_recommendation')}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{ color: textPrimary, lineHeight: 1.7 }}
                >
                  {rec.overall_recommendation}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </CustomMuiThemeProvider>
  );
}
